import {
  CountryCode,
  getCountries,
  isValidNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js"

export function isValidPhoneNumber(phoneNumber: string, countryCode?: string, locale?: string) {
  const cleanPhoneNumber = cleanPhoneNumberInput(phoneNumber)

  let isValid = isValidNumber(cleanPhoneNumber, validateCountryCode(countryCode))

  if (!isValid && !cleanPhoneNumber.includes("+")) {
    isValid = isValidNumber(`+${cleanPhoneNumber}`, validateCountryCode(countryCode))
  }

  if (!isValid && !countryCode && locale) {
    const countryCodeFromLocale = locale.replace("-", "_").split("_").at(-1)
    isValid = isValidNumber(cleanPhoneNumber, validateCountryCode(countryCodeFromLocale))
  }

  return isValid
}

export function parsePhoneNumber(phoneNumber: string, countryCode?: string, locale?: string) {
  const cleanPhoneNumber = cleanPhoneNumberInput(phoneNumber)

  // Try as-is first (handles well-formed E.164 like +34618254038)
  let parsed = parsePhoneNumberFromString(cleanPhoneNumber)
  if (parsed?.isValid()) return parsed

  // Try with provided country code (handles national numbers like "618254038" + country "ES")
  if (countryCode) {
    parsed = parsePhoneNumberFromString(cleanPhoneNumber, {
      defaultCountry: validateCountryCode(countryCode),
    })
    if (parsed?.isValid()) return parsed
  }

  // Strip '+' and retry with country code — handles phones like "+618254038" where '+' is
  // present but the country calling code is missing (should be "+34618254038" for Spain)
  if (countryCode && cleanPhoneNumber.startsWith("+")) {
    parsed = parsePhoneNumberFromString(cleanPhoneNumber.slice(1), {
      defaultCountry: validateCountryCode(countryCode),
    })
    if (parsed?.isValid()) return parsed
  }

  // Try adding '+' prefix for numbers without it
  if (!cleanPhoneNumber.startsWith("+")) {
    parsed = parsePhoneNumberFromString(`+${cleanPhoneNumber}`)
    if (parsed?.isValid()) return parsed
  }

  // Last resort: derive country from locale
  if (!countryCode && locale) {
    const countryCodeFromLocale = locale.replace("-", "_").split("_").at(-1)
    parsed = parsePhoneNumberFromString(cleanPhoneNumber, {
      defaultCountry: validateCountryCode(countryCodeFromLocale),
    })
    if (parsed?.isValid()) return parsed
  }

  // Handle a stray '+' that is not at the start (e.g. "34+722107662", where the calling code
  // and national number were typed with the '+' misplaced). Strip every '+' and resolve again;
  // the stripped value has no stray '+', so this recurses at most once.
  const hasStrayPlus = cleanPhoneNumber.replace(/^\+/, "").includes("+")
  if (hasStrayPlus) {
    return parsePhoneNumber(cleanPhoneNumber.replace(/\+/g, ""), countryCode, locale)
  }

  return null
}

function cleanPhoneNumberInput(phoneNumber: string) {
  const cleanPhoneNumber = phoneNumber.trim().replace(/\s+/g, "").replace(/^00/, "+")

  return normalizeMexicoWhatsappMobileToken(cleanPhoneNumber)
}

function normalizeMexicoWhatsappMobileToken(phoneNumber: string) {
  const hasPlus = phoneNumber.startsWith("+")
  const digits = hasPlus ? phoneNumber.slice(1) : phoneNumber

  if (!/^521\d{10}$/.test(digits)) {
    return phoneNumber
  }

  return `${hasPlus ? "+" : ""}52${digits.slice(3)}`
}

export function formatPhoneNumber(
  phoneNumber: string,
  countryCode?: string,
  locale?: string,
): string {
  const parsed = parsePhoneNumber(phoneNumber, countryCode, locale)

  if (!parsed) {
    // Fallback to original if we can't parse it
    return phoneNumber
  }

  // Format as international number for consistency
  return parsed.formatInternational()
}

function validateCountryCode(countryCode?: string) {
  if (!countryCode) return undefined
  return getCountries().includes(countryCode as CountryCode)
    ? (countryCode as CountryCode)
    : undefined
}

export const getCallingCode = (raw: string): string | null => {
  // Normalize: remove spaces/dashes/etc and ensure it starts with +
  const cleaned = raw.replace(/[^\d+]/g, "").replace(/^00/, "+")
  const withPlus = cleaned.startsWith("+") ? cleaned : `+${cleaned}`

  const phone = parsePhoneNumberFromString(withPlus)
  return phone?.countryCallingCode ?? null
}
